import {
  CampaignFilterModel,
  CampaignStatusFilter,
  useCampaignFilter,
  useSetCampaignFilter,
} from '@gripp/shared-logic';
import { tokens } from '@gripp/shared-ui';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';

const STATUSES: CampaignStatusFilter[] = ['DRAFT', 'SCHEDULED', 'SENT'];

export const CampaignsFilterPanel = () => {
  const { t } = useTranslation();
  const { data: filter } = useCampaignFilter();
  const { mutateAsync: setFilter } = useSetCampaignFilter();

  const onSelect = async (status: CampaignStatusFilter | undefined) => {
    if (!filter) return;
    const next = new CampaignFilterModel({
      ...filter,
      status:
        status === undefined || filter.status === status ? undefined : status,
    });
    await setFilter(next);
  };

  const isAllSelected = filter?.status === undefined;

  return (
    <View style={styles.container}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>
          {t('campaigns.filter.status', 'Status')}
        </Text>
        <View style={styles.optionsContainer}>
          <Pressable
            onPress={() => onSelect(undefined)}
            style={[styles.optionChip, isAllSelected && styles.optionChipSelected]}
            accessibilityRole="button"
            accessibilityLabel={t('campaigns.filter.all', 'All')}
            testID="campaign-filter-status-all"
          >
            <Text
              style={[
                styles.optionChipText,
                isAllSelected && styles.optionChipTextSelected,
              ]}
            >
              {t('campaigns.filter.all', 'All')}
            </Text>
          </Pressable>
          {STATUSES.map((s) => {
            const isSelected = filter?.status === s;
            const label = t(
              `campaigns.status.${s.toLowerCase()}`,
              s.charAt(0) + s.slice(1).toLowerCase()
            );
            return (
              <Pressable
                key={s}
                onPress={() => onSelect(s)}
                style={[styles.optionChip, isSelected && styles.optionChipSelected]}
                accessibilityRole="button"
                accessibilityLabel={label}
                testID={`campaign-filter-status-${s.toLowerCase()}`}
              >
                <Text
                  style={[
                    styles.optionChipText,
                    isSelected && styles.optionChipTextSelected,
                  ]}
                >
                  {label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: tokens.spacing.lg,
  },
  section: {
    paddingTop: tokens.spacing['2xl'],
  },
  sectionTitle: {
    ...tokens.typography.label.largeEmphasised,
    fontWeight: tokens.typography.label.largeEmphasised.fontWeight,
    color: tokens.colors.neutral[600],
    marginBottom: tokens.spacing.md,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  optionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
  },
  optionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: 10,
    borderRadius: tokens.borderRadius['3xl'],
    backgroundColor: tokens.colors.neutral[200],
    gap: 6,
  },
  optionChipSelected: {
    backgroundColor: tokens.colors.neutral[800],
    borderColor: tokens.colors.neutral[800],
  },
  optionChipText: {
    ...tokens.typography.label.large,
    fontWeight: tokens.typography.label.largeEmphasised.fontWeight,
    color: tokens.colors.neutral[800],
  },
  optionChipTextSelected: {
    color: tokens.colors.brand.white,
  },
});
